import type { FormValues, FormInstance } from '../types';
import { loadFormState } from './storage';

/**
 * List of form-level field IDs that should always be merged from localStorage
 * These are fields that are set early in the form and used in conditions throughout
 */
export const FORM_LEVEL_FIELDS: readonly string[] = [
  'willType',
  'estimatedPropertyValue',
  'estimatedCashEstateValue',
  'charityPercentage',
  'cashGiftAmount',
  'cashGiftToSomeoneAmount',
  'childAmount',
  'hasChildren',
  'hasChildrenFromPreviousRelationship',
];

/**
 * Merges form-level values from localStorage and completed form instances
 * This ensures that form-level fields like willType are available for condition evaluation
 *
 * @param values - Current form values
 * @param completedForms - Completed form instances
 * @param localStorageKey - Optional localStorage key to load from
 * @param logWarnings - Whether to log warnings on errors (default: false)
 * @returns Merged values object
 */
export function mergeFormValues(
  values: FormValues | Record<string, unknown>,
  completedForms: Record<string, FormInstance[]>,
  localStorageKey?: string,
  logWarnings = false
): FormValues | Record<string, unknown> {
  // Start with current values (most up-to-date)
  const merged = { ...values };

  // Merge from localStorage
  if (localStorageKey) {
    try {
      const stored = loadFormState(localStorageKey);
      if (stored?.values) {
        // For form-level fields, always use localStorage value if it exists
        FORM_LEVEL_FIELDS.forEach((fieldId) => {
          if (
            stored.values![fieldId] !== undefined &&
            stored.values![fieldId] !== null &&
            stored.values![fieldId] !== ''
          ) {
            const current = merged[fieldId];
            const currentEmpty = current === undefined || current === null || current === '';
            // Estate totals: only fill from localStorage when not already set in memory.
            // Otherwise stale persisted values overwrite the live state / completed estate step.
            const isEstateTotalField =
              fieldId === 'estimatedPropertyValue' || fieldId === 'estimatedCashEstateValue';
            if (!isEstateTotalField || currentEmpty) {
              merged[fieldId] = stored.values![fieldId];
            }
          }
        });

        // For other fields, merge from localStorage only if not in current values
        Object.keys(stored.values).forEach((key) => {
          if (!FORM_LEVEL_FIELDS.includes(key)) {
            if (merged[key] === undefined && stored.values![key] !== undefined) {
              merged[key] = stored.values![key];
            }
          }
        });
      }
    } catch (e) {
      // Only log warning if requested (for useSectionVisibility)
      if (logWarnings) {
        console.warn('Failed to load form state from localStorage for condition evaluation:', e);
      }
    }
  }

  // Merge from completedForms
  Object.values(completedForms).forEach((instances) => {
    instances.forEach((instance) => {
      Object.keys(instance.data).forEach((key) => {
        if (!FORM_LEVEL_FIELDS.includes(key)) {
          if (merged[key] === undefined && instance.data[key] !== undefined) {
            merged[key] = instance.data[key];
          }
        }
      });
    });
  });

  return merged;
}
